import React from 'react';

import { Box, Chip, Stack, TextField, Typography } from '@mui/material';

import type { SocieteRewardCopayPrice } from '~/services/LuxLoyalty';

import { formatExtraNightRate } from './formatExtraNightRate';

interface Props {
  currency: string;
  /** Current form value for this currency's base/extra-night co-pay override. Empty string = untouched. */
  value: { base: string; extraNight: string };
  /** The package's current live co-pay price for this currency, shown as placeholder text. */
  livePrice: SocieteRewardCopayPrice | undefined;
  onChange: (field: 'base' | 'extraNight', value: string) => void;
  /**
   * Whether this currency's co-pay is currently admin-overridden (per `override_meta.fields`).
   * Shows an "Overridden" chip next to the currency code when true. `SocieteRewardCopayPrice`
   * doesn't carry a per-currency "computed" shadow value the way points do, so — unlike the points
   * fields — there's no "Formula: …" helper text to show for a non-overridden currency either.
   */
  isOverridden?: boolean;
}

/** One currency's co-pay override row (base + extra-night fields) inside the override dialog. */
export default function CopayOverrideRow({ currency, value, livePrice, onChange, isOverridden = false }: Props) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '130px 1fr 1fr', gap: 1.5, alignItems: 'center' }}>
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
        <Typography variant="body2">{currency}</Typography>
        {isOverridden && <Chip label="Overridden" size="small" color="warning" variant="outlined" />}
      </Stack>
      <TextField
        label="Base copay"
        size="small"
        value={value.base}
        onChange={(e) => onChange('base', e.target.value)}
        placeholder={String(livePrice?.societe_reward_copay_base ?? 0)}
      />
      <TextField
        label="Extra-night copay"
        size="small"
        value={value.extraNight}
        onChange={(e) => onChange('extraNight', e.target.value)}
        placeholder={livePrice ? formatExtraNightRate(livePrice.societe_reward_copay_extra_night ?? null) : '0'}
      />
    </Box>
  );
}
